import React, { Component } from "react";
import { RouteComponentProps, generatePath } from "react-router";
import DashboardLayout, { DashboardTab } from "layouts/Dashboard";
import { logPageView } from "services/analytics";
import RouteURLs from "RouteURLs";
import NextRouteURLs from 'NextRouteURLs';
import { ExpandedBooking } from "services/entities/booking";
import { loadExpandedBookings } from "services/bookings";
import { handleError } from "util/errors";
import { DateTime, TimeSpan } from "util/time";
import { BookingIcon } from "components/Icon";
import "./index.scss";
import NewResourceButton from "components/NewResourceButton";
import {
  formatBookingDate,
  formatBookingTime,
  formatTimeUntilBooking,
} from "util/booking";
import ProgressSpinner from "components/ProgressSpinner";
import BookingCard from "components/bookings/BookingCard";
import {
  Typography,
  TableContainer,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TablePagination,
} from "@material-ui/core";
import { Link } from "react-router-dom";

interface BookingsListState {
  futureBookings: ExpandedBooking[] | null;
  pastBookings: ExpandedBooking[] | null;
  pastBookingsPage: number;
}

interface BookingsListPageProps extends RouteComponentProps {}

export default class BookingsListPage extends Component<
  BookingsListPageProps,
  BookingsListState
> {
  private static readonly PAST_BOOKINGS_PER_PAGE = 10;

  state: BookingsListState = {
    futureBookings: null,
    pastBookings: null,
    pastBookingsPage: 0,
  };

  componentDidMount(): void {
    logPageView(RouteURLs.BOOKINGS_LIST, "Bookings");
    const currentTime = DateTime.now();
    loadExpandedBookings({ minTime: currentTime }).then(
      (bookings) => this.setState({ futureBookings: bookings }),
      (err) => handleError("Failed to load bookings", err)
    );
    loadExpandedBookings({ maxTime: currentTime }).then(
      (bookings) => this.setState({ pastBookings: bookings }),
      (err) => handleError("Failed to load bookings", err)
    );
  }

  onNewBookingClick = () => {
    this.props.history.push(NextRouteURLs.SPACES);
  };

  onSpacesClick = () => {
    this.props.history.push(NextRouteURLs.SPACES);
  };

  getTimeUntilNextBooking(): TimeSpan | null {
    const futureBookings: ExpandedBooking[] | null = this.state.futureBookings;
    if (!futureBookings || futureBookings!.length === 0) {
      return null;
    }
    const nextBooking = futureBookings![0];
    return nextBooking.startTime.diff(DateTime.now());
  }

  renderFutureBookings() {
    const bookings: ExpandedBooking[] | null = this.state.futureBookings;
    const timeUntilNextBooking = this.getTimeUntilNextBooking();
    return (
      <section className="upcoming">
        <Typography variant="h2">My Upcoming Bookings</Typography>
        {timeUntilNextBooking && (
          <div className="next-booking-time">
            You have an upcoming booking in{" "}
            <span className="time">
              {formatTimeUntilBooking(timeUntilNextBooking)}
            </span>
          </div>
        )}
        {bookings === null && <ProgressSpinner />}
        {bookings && bookings!.length > 0 && (
          <div className="bookings">
            {bookings!.map((b) => (
              <BookingCard booking={b} key={b.code} />
            ))}
          </div>
        )}
        {bookings && bookings!.length === 0 && (
          <div className="bookings empty">
            <BookingIcon />
            <Typography variant="body1">
              You have no upcoming bookings.
            </Typography>
          </div>
        )}
      </section>
    );
  }

  onPastBookingsPageChanged = (ev: any, page: number) => {
    this.setState({ pastBookingsPage: page });
  };

  renderPastBookings() {
    // TODO: server pagination
    const bookings: ExpandedBooking[] | null = this.state.pastBookings;
    const page = this.state.pastBookingsPage;
    const bookingsPerPage = BookingsListPage.PAST_BOOKINGS_PER_PAGE;
    let pagedBookings: ExpandedBooking[] | null = null;
    if (bookings) {
      const startIndex = page * bookingsPerPage;
      const endIndex = Math.min(startIndex + bookingsPerPage, bookings!.length);
      if (startIndex >= bookings!.length) {
        pagedBookings = [];
      } else {
        pagedBookings = bookings!.slice(startIndex, endIndex);
      }
    }
    return (
      <section className="past">
        <Typography variant="h2">Previous bookings</Typography>
        {bookings === null && <ProgressSpinner />}
        {bookings && bookings!.length > 0 && (
          <>
            <TableContainer className="bookings">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Time Period</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Design</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedBookings!.map((b) => (
                    <TableRow key={b.code}>
                      <TableCell>{formatBookingDate(b)}</TableCell>
                      <TableCell>{formatBookingTime(b)}</TableCell>
                      <TableCell>{b.space.name}</TableCell>
                      <TableCell>{b.design ? b.design.name : ""}</TableCell>
                      <TableCell>
                        <Link
                          className="view"
                          to={generatePath(RouteURLs.EDIT_BOOKING_SUCCESS, {
                            code: b.code,
                          })}
                        >
                          View
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              className="bookings-pagination"
              count={bookings!.length}
              rowsPerPageOptions={[bookingsPerPage]}
              rowsPerPage={bookingsPerPage}
              page={page}
              labelDisplayedRows={(paginationInfo) =>
                `Showing ${paginationInfo.from} - ${paginationInfo.to} of ${paginationInfo.count}`
              }
              onChangePage={this.onPastBookingsPageChanged}
            />
          </>
        )}
        {bookings && bookings!.length === 0 && (
          <div className="bookings empty">
            <BookingIcon />
            <Typography variant="body1">You have no past bookings.</Typography>
          </div>
        )}
      </section>
    );
  }

  render() {
    return (
      <DashboardLayout
        id="list-bookings-page"
        currentTab={DashboardTab.Bookings}
      >
        <NewResourceButton
          className="new-booking"
          label="Create a new booking"
          note="Book your very own space by the hour"
          onClick={this.onNewBookingClick}
        />
        {this.renderFutureBookings()}
        {this.renderPastBookings()}
      </DashboardLayout>
    );
  }
}
